/**
 * InMotion Service Centre — Main Theme JavaScript
 * Pure vanilla JS, no external dependencies.
 * Handles: header, cart drawer, predictive search, scroll animations,
 * parallax, counters, product page tabs/gallery/variants, collection
 * filters, announcement bar, email popup, quick view, FAQ accordion.
 */

document.addEventListener('DOMContentLoaded', function () {

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('video[autoplay]').forEach(function (video) {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Utility helpers                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Debounce — delay execution until calls stop for `delay` ms.
   * @param {Function} fn
   * @param {number} delay
   * @returns {Function}
   */
  function debounce(fn, delay) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  }

  /**
   * Format cents into a dollar string ($X.XX).
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  /* Expose globally so inline section scripts (device grade selector) can use it */
  window.Shopify = window.Shopify || {};
  if (!window.Shopify.formatMoney) {
    window.Shopify.formatMoney = formatMoney;
  }

  /**
   * Fetch a Shopify section via the Section Rendering API.
   * @param {string} url — page URL to request
   * @param {string} sectionId — the section id to isolate
   * @returns {Promise<string>} section HTML
   */
  function fetchSection(url, sectionId) {
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var sectionUrl = url + separator + 'section_id=' + sectionId;
    return fetch(sectionUrl).then(function (r) {
      if (!r.ok) throw new Error('Section fetch failed: ' + r.status);
      return r.text();
    });
  }

  /**
   * Trap keyboard focus inside an element (for modals / drawers).
   * Returns a cleanup function to remove the listener.
   * @param {HTMLElement} element
   * @returns {Function} release
   */
  function trapFocus(element) {
    var focusable = element.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    first.focus();

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    element.addEventListener('keydown', handler);
    return function () { element.removeEventListener('keydown', handler); };
  }

  /* ------------------------------------------------------------------ */
  /*  Sticky header                                                     */
  /* ------------------------------------------------------------------ */

  var header = document.querySelector('.site-header');
  if (header) {
    window.addEventListener('scroll', function () {
      header.classList.toggle('header--scrolled', window.scrollY > 50);
    }, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /*  Mobile menu                                                       */
  /* ------------------------------------------------------------------ */

  var menuToggle = document.querySelector('[data-mobile-menu-toggle]');
  var mobileMenu = document.querySelector('[data-mobile-drawer]');
  var menuOverlay = document.querySelector('[data-mobile-drawer-overlay]');
  var menuClose = document.querySelector('[data-mobile-menu-close]');
  var releaseFocusMenu;

  function openMobileMenu() {
    if (mobileMenu) {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      menuToggle.setAttribute('aria-expanded', 'true');
      menuToggle.setAttribute('aria-label', 'Close menu');
      document.body.style.overflow = 'hidden';
      releaseFocusMenu = trapFocus(mobileMenu);
    }
  }
  function closeMobileMenu() {
    if (mobileMenu) {
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      menuToggle.setAttribute('aria-expanded', 'false');
      menuToggle.setAttribute('aria-label', 'Open menu');
      document.body.style.overflow = '';
      if (typeof releaseFocusMenu === 'function') releaseFocusMenu();
      menuToggle.focus();
    }
  }

  if (menuToggle) menuToggle.addEventListener('click', openMobileMenu);
  if (menuClose) menuClose.addEventListener('click', closeMobileMenu);
  if (menuOverlay) menuOverlay.addEventListener('click', closeMobileMenu);

  /* Mobile drawer: expand/collapse sub-lists */
  document.querySelectorAll('[data-drawer-expand]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var sublist = this.parentElement.querySelector('[data-drawer-sublist]');
      if (sublist) sublist.classList.toggle('is-open');
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Mega-menu (desktop hover)                                         */
  /* ------------------------------------------------------------------ */

  var megaMenuItems = document.querySelectorAll('.has-mega-menu');
  megaMenuItems.forEach(function (item) {
    var timeout;
    item.addEventListener('mouseenter', function () {
      clearTimeout(timeout);
      timeout = setTimeout(function () { item.classList.add('mega-menu--open'); }, 150);
    });
    item.addEventListener('mouseleave', function () {
      clearTimeout(timeout);
      timeout = setTimeout(function () { item.classList.remove('mega-menu--open'); }, 150);
    });
  });

  /* ------------------------------------------------------------------ */
  /*  Cart drawer                                                       */
  /* ------------------------------------------------------------------ */

  var cartDrawer = document.querySelector('.cart-drawer');
  var cartCountBadge = document.querySelector('.js-cart-count');
  var releaseFocusCart;

  /** Keep aria-hidden and the overlay scrim in sync with the open state
   *  (the overlay lives inside the section, so refresh re-stamps both). */
  function syncDrawerState(open) {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.getElementById('cart-drawer-overlay');
    if (drawer) drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (overlay) {
      overlay.classList.toggle('is-visible', open);
      overlay.setAttribute('aria-hidden', 'true');
    }
  }

  function openCartDrawer() {
    document.body.classList.add('cart-drawer--open');
    syncDrawerState(true);
    if (cartDrawer) releaseFocusCart = trapFocus(cartDrawer);
  }

  function closeCartDrawer() {
    document.body.classList.remove('cart-drawer--open');
    syncDrawerState(false);
    if (typeof releaseFocusCart === 'function') releaseFocusCart();
  }

  function updateCartCount(count) {
    if (cartCountBadge) {
      cartCountBadge.textContent = count;
      cartCountBadge.style.display = count > 0 ? '' : 'none';
    }
  }

  /** Re-render the complete cart section so empty/items/footer stay in sync. */
  function refreshCartDrawer() {
    return Promise.all([
      fetchSection('/cart', 'cart-drawer'),
      fetch('/cart.js').then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
    ]).then(function (results) {
      var rendered = new DOMParser().parseFromString(results[0], 'text/html');
      var currentSection = document.getElementById('shopify-section-cart-drawer');
      var nextSection = rendered.getElementById('shopify-section-cart-drawer');
      if (!currentSection || !nextSection) throw new Error('Cart drawer section missing');
      currentSection.replaceWith(nextSection);
      cartDrawer = document.querySelector('.cart-drawer');
      syncDrawerState(document.body.classList.contains('cart-drawer--open'));
      updateCartCount(results[1].item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: results[1] }));
    });
  }

  /** Refresh the cart page section (no-op when not on /cart). */
  function refreshCartPage() {
    var cartPage = document.querySelector('.cart-page[data-section-id]');
    if (!cartPage) return Promise.resolve();
    var sectionId = cartPage.getAttribute('data-section-id');
    return fetchSection('/cart', sectionId).then(function (html) {
      var rendered = new DOMParser().parseFromString(html, 'text/html');
      var current = document.getElementById('shopify-section-' + sectionId);
      var next = rendered.getElementById('shopify-section-' + sectionId);
      if (current && next) current.replaceWith(next);
    });
  }

  function refreshCartUI() {
    return Promise.all([refreshCartDrawer(), refreshCartPage()]).then(function () {
      /* Re-rendered sections contain fresh .affirm-as-low-as elements */
      if (window.affirm && affirm.ui && typeof affirm.ui.refresh === 'function') {
        affirm.ui.ready(function () { affirm.ui.refresh(); });
      }
    });
  }

  function setCartLoading(on) {
    var drawer = document.querySelector('.cart-drawer');
    if (drawer) drawer.classList.toggle('is-loading', on);
    var cartPage = document.querySelector('.cart-page');
    if (cartPage) cartPage.classList.toggle('is-loading', on);
  }

  /* Toast — user-facing feedback for cart mutations (errors, remove + undo). */
  var cartToastTimer;
  function hideCartToast() {
    var toast = document.querySelector('.cart-toast');
    if (toast) toast.classList.remove('is-visible');
  }
  function showCartToast(message, actionLabel, onAction) {
    var toast = document.querySelector('.cart-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'cart-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    toast.appendChild(text);
    if (actionLabel && onAction) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-toast__action';
      btn.textContent = actionLabel;
      btn.addEventListener('click', function () {
        hideCartToast();
        onAction();
      });
      toast.appendChild(btn);
    }
    toast.classList.add('is-visible');
    /* The toast sits outside the drawer's focus trap — move focus to the
       action so keyboard users inside the drawer can still reach Undo. */
    if (actionLabel && document.body.classList.contains('cart-drawer--open')) {
      var actionBtn = toast.querySelector('.cart-toast__action');
      if (actionBtn) actionBtn.focus();
    }
    clearTimeout(cartToastTimer);
    // ponytail: longer window when there's an action to take (undoing a four-figure removal)
    cartToastTimer = setTimeout(hideCartToast, actionLabel ? 9000 : 6000);
  }

  /* Cart drawer open / close listeners (event delegation) */
  document.addEventListener('click', function (e) {
    if (e.target.closest('.js-cart-icon')) {
      e.preventDefault();
      /* Open immediately so the click always responds; refresh in the background. */
      openCartDrawer();
      setCartLoading(true);
      refreshCartDrawer()
        .then(function () { setCartLoading(false); })
        .catch(function () {
          setCartLoading(false);
          showCartToast("Couldn't refresh your cart — please try again.");
        });
    }
    if (e.target.closest('.js-cart-drawer-close') || e.target.closest('.cart-drawer__overlay')) closeCartDrawer();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.classList.contains('cart-drawer--open')) closeCartDrawer();
  });

  /* AJAX add to cart — intercept product forms */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    var data = new FormData(form);
    fetch('/cart/add.js', { method: 'POST', body: data })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (body) {
            throw new Error(body.description || body.message || r.status);
          });
        }
        return r.json();
      })
      .then(refreshCartUI)
      .then(openCartDrawer)
      .catch(function (err) {
        showCartToast(err && err.message && isNaN(err.message) ? err.message : "Couldn't add to cart — please try again.");
      });
  });

  /* AJAX quantity change & remove — refresh drawer + cart page afterward. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.cart-drawer form[action="/cart/change"], .cart-page form[action="/cart/change"]');
    if (!form) return;
    e.preventDefault();
    var data = new FormData(form);
    if (e.submitter && e.submitter.name) data.set(e.submitter.name, e.submitter.value);
    var isRemove = String(data.get('quantity')) === '0';
    var undoVariant = form.getAttribute('data-variant-id');
    var undoQuantity = Number(form.getAttribute('data-item-quantity')) || 1;
    var undoTitle = form.getAttribute('data-item-title');
    setCartLoading(true);
    fetch('/cart/change.js', { method: 'POST', body: data })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(refreshCartUI)
      .then(function () {
        setCartLoading(false);
        if (isRemove && undoVariant) {
          showCartToast((undoTitle || 'Item') + ' removed', 'Undo', function () {
            setCartLoading(true);
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: Number(undoVariant), quantity: undoQuantity })
            })
              .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
              .then(refreshCartUI)
              .then(function () { setCartLoading(false); })
              .catch(function () {
                setCartLoading(false);
                showCartToast("Couldn't restore the item — please re-add it.");
              });
          });
        }
      })
      .catch(function () {
        setCartLoading(false);
        showCartToast("Couldn't update your cart — please try again.");
      });
  });

  /* ------------------------------------------------------------------ */
  /*  Predictive search                                                 */
  /* ------------------------------------------------------------------ */

  var searchModal = document.querySelector('.search-modal');
  var searchToggle = document.querySelector('[data-search-toggle]');
  var searchInput = document.querySelector('.js-search-input');
  var searchResults = document.querySelector('.js-search-results');
  var releaseFocusSearch;
  var searchActiveIndex = -1;

  function openSearchModal() {
    if (searchModal) {
      searchModal.classList.add('is-open');
      searchModal.setAttribute('aria-hidden', 'false');
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'true');
      releaseFocusSearch = trapFocus(searchModal);
      if (searchInput) searchInput.focus();
    }
  }

  function closeSearchModal() {
    if (searchModal) {
      searchModal.classList.remove('is-open');
      searchModal.setAttribute('aria-hidden', 'true');
    }
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
    if (typeof releaseFocusSearch === 'function') releaseFocusSearch();
    if (searchResults) searchResults.innerHTML = '';
    searchActiveIndex = -1;
    if (searchToggle) searchToggle.focus();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('.js-search-icon')) { e.preventDefault(); openSearchModal(); return; }
    if (e.target.closest('.js-search-close')) { closeSearchModal(); return; }
    if (searchModal && searchModal.classList.contains('is-open') && !e.target.closest('.search-modal__inner')) closeSearchModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && searchModal && searchModal.classList.contains('is-open')) closeSearchModal();
  });

  if (searchInput) {
    searchInput.addEventListener('input', debounce(function () {
      var q = searchInput.value.trim();
      if (!q) { if (searchResults) searchResults.innerHTML = ''; return; }
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = data.resources && data.resources.results && data.resources.results.products ? data.resources.results.products : [];
          if (!searchResults) return;
          if (!products.length) { searchResults.innerHTML = '<p class="search-no-results">No results found.</p>'; return; }
          searchResults.innerHTML = products.map(function (p) {
            var img = p.image ? '<div class="search__result-image"><img src="' + p.image + '" alt="" width="48" height="48" loading="lazy"></div>' : '';
            return '<a href="' + p.url + '" class="search__result-item search-result-item">' + img + '<span class="search__result-title">' + p.title + '</span></a>';
          }).join('');
          searchActiveIndex = -1;
        })
        .catch(function () { if (searchResults) searchResults.innerHTML = '<p class="search-no-results">Search unavailable.</p>'; });
    }, 300));

    /* Arrow key navigation through search results */
    searchInput.addEventListener('keydown', function (e) {
      if (!searchResults) return;
      var items = searchResults.querySelectorAll('.search-result-item');
      if (!items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); searchActiveIndex = Math.min(searchActiveIndex + 1, items.length - 1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); searchActiveIndex = Math.max(searchActiveIndex - 1, 0); }
      if (e.key === 'Enter' && searchActiveIndex >= 0) { e.preventDefault(); items[searchActiveIndex].click(); return; }
      items.forEach(function (it, i) { it.classList.toggle('search-result-item--active', i === searchActiveIndex); });
      if (items[searchActiveIndex]) items[searchActiveIndex].scrollIntoView({ block: 'nearest' });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Scroll fade-up animations (IntersectionObserver)                  */
  /* ------------------------------------------------------------------ */

  var fadeEls = document.querySelectorAll('.fade-up');
  if (fadeEls.length && 'IntersectionObserver' in window) {
    var fadeObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          fadeObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    fadeEls.forEach(function (el) { fadeObserver.observe(el); });
  }

  /* ------------------------------------------------------------------ */
  /*  Hero parallax                                                     */
  /* ------------------------------------------------------------------ */

  function initParallax() {
    var parallaxEls = document.querySelectorAll('[data-parallax]');
    if (!parallaxEls.length || window.innerWidth <= 768) return;
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var scrollY = window.scrollY;
        parallaxEls.forEach(function (el) {
          el.style.transform = 'translateY(' + (scrollY * 0.3) + 'px)';
        });
        ticking = false;
      });
    }, { passive: true });
  }
  initParallax();

  /* ------------------------------------------------------------------ */
  /*  Counter animation                                                 */
  /* ------------------------------------------------------------------ */

  var counters = document.querySelectorAll('[data-count-to]');
  if (counters.length && 'IntersectionObserver' in window) {
    var counterObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        counterObserver.unobserve(entry.target);
        var target = parseInt(entry.target.getAttribute('data-count-to'), 10);
        var duration = 1500; // ms
        var start = performance.now();
        function step(now) {
          var progress = Math.min((now - start) / duration, 1);
          var current = Math.floor(progress * target);
          entry.target.textContent = current.toLocaleString();
          if (progress < 1) requestAnimationFrame(step);
          else entry.target.textContent = target.toLocaleString();
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.1 });
    counters.forEach(function (el) { counterObserver.observe(el); });
  }

  /* ------------------------------------------------------------------ */
  /*  Product page tabs                                                 */
  /* ------------------------------------------------------------------ */

  var tabList = document.querySelector('[role="tablist"]');
  if (tabList) {
    tabList.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (!tab) return;
      var panelId = tab.getAttribute('aria-controls');
      /* Deactivate all tabs */
      tabList.querySelectorAll('[role="tab"]').forEach(function (t) {
        t.classList.remove('tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      /* Hide all panels */
      var tabContainer = tabList.closest('.product-tabs');
      if (tabContainer) {
        tabContainer.querySelectorAll('[role="tabpanel"]').forEach(function (p) { p.hidden = true; });
      }
      /* Activate clicked tab and its panel */
      tab.classList.add('tab--active');
      tab.setAttribute('aria-selected', 'true');
      var panel = document.getElementById(panelId);
      if (panel) panel.hidden = false;
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Product image gallery                                             */
  /* ------------------------------------------------------------------ */

  var mainImage = document.querySelector('.js-product-main-image');
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('.js-product-thumbnail');
    if (!thumb || !mainImage) return;
    var src = thumb.getAttribute('data-full-src') || thumb.src;
    var alt = thumb.getAttribute('alt') || '';
    mainImage.src = src;
    mainImage.alt = alt;
    /* Mark active thumbnail */
    document.querySelectorAll('.js-product-thumbnail').forEach(function (t) { t.classList.remove('thumbnail--active'); });
    thumb.classList.add('thumbnail--active');
  });

  /* ------------------------------------------------------------------ */
  /*  Variant selector                                                  */
  /* ------------------------------------------------------------------ */

  var variantJsonEl = document.querySelector('[data-product-json]');
  if (variantJsonEl) {
    try {
      var productData = JSON.parse(variantJsonEl.textContent);
      var variants = Array.isArray(productData) ? productData : (productData.variants || []);
      var variantSelector = document.querySelector('[data-variant-selector]');
      var hiddenSelect = document.querySelector('[data-variant-select]');

      function getSelectedOptionsFromButtons() {
        var options = [];
        var fieldsets = document.querySelectorAll('.variant-selector__option');
        fieldsets.forEach(function (fs) {
          var selected = fs.querySelector('.variant-selector__button--selected, .variant-selector__swatch--selected');
          options.push(selected ? selected.getAttribute('data-option-value') : '');
        });
        return options;
      }

      function findVariant(options) {
        return variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === options[i]; });
        });
      }

      function updateVariantUI(variant) {
        var priceEl = document.querySelector('.price__current');
        var compareEl = document.querySelector('.price__compare');
        var priceBlock = document.querySelector('[data-product-price]');
        var addBtn = document.querySelector('.js-add-to-cart, [data-add-to-cart]');
        var variantInput = document.querySelector('input[name="id"]');

        /* Hide entire price block when variant is sold out */
        if (priceBlock) {
          priceBlock.style.display = (variant && variant.available) ? '' : 'none';
        }

        if (priceEl && variant) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        if (addBtn) {
          if (!variant) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
          else if (!variant.available) { addBtn.disabled = true; addBtn.textContent = 'Sold Out'; }
          else { addBtn.disabled = false; addBtn.textContent = 'Add to Cart'; }
        }
        if (variantInput && variant) variantInput.value = variant.id;
        if (hiddenSelect && variant) hiddenSelect.value = variant.id;
        /* Update URL */
        if (variant) {
          var url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          history.replaceState(null, '', url.toString());
        }
      }

      /* Button-driven variant selection (from variant-selector.liquid snippet) */
      if (variantSelector) {
        variantSelector.addEventListener('click', function (e) {
          var btn = e.target.closest('.variant-selector__button, .variant-selector__swatch');
          if (!btn) return;

          /* Toggle selection within this option group */
          var fieldset = btn.closest('.variant-selector__option');
          if (fieldset) {
            fieldset.querySelectorAll('.variant-selector__button, .variant-selector__swatch').forEach(function (b) {
              b.classList.remove('variant-selector__button--selected', 'variant-selector__swatch--selected');
              b.setAttribute('aria-pressed', 'false');
            });
            var selectedClass = btn.classList.contains('variant-selector__swatch')
              ? 'variant-selector__swatch--selected'
              : 'variant-selector__button--selected';
            btn.classList.add(selectedClass);
            btn.setAttribute('aria-pressed', 'true');

            /* Update selected value display */
            var optionIndex = btn.getAttribute('data-option-index');
            var selectedLabel = fieldset.querySelector('[data-option-selected="' + optionIndex + '"]');
            if (selectedLabel) selectedLabel.textContent = btn.getAttribute('data-option-value');
          }

          var variant = findVariant(getSelectedOptionsFromButtons());
          updateVariantUI(variant);
        });
      }

      /* Legacy select-driven variant selection (fallback) */
      var optionSelectors = document.querySelectorAll('.js-variant-option');
      optionSelectors.forEach(function (sel) {
        sel.addEventListener('change', function () {
          var options = [];
          optionSelectors.forEach(function (s) { options.push(s.value); });
          var variant = findVariant(options);
          updateVariantUI(variant);
        });
      });
    } catch (err) {
      console.error('Variant selector init failed', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Quantity selector (+/- buttons)                                   */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-quantity-minus]');
    var plus = e.target.closest('[data-quantity-plus]');
    if (!minus && !plus) return;

    var wrapper = (minus || plus).closest('[data-quantity-selector]');
    if (!wrapper) return;
    var input = wrapper.querySelector('[data-quantity-input]');
    if (!input) return;

    var current = parseInt(input.value, 10) || 1;
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var max = parseInt(input.getAttribute('max'), 10) || 9999;

    if (minus) input.value = Math.max(min, current - 1);
    if (plus) input.value = Math.min(max, current + 1);

    /* Trigger change event for any listeners */
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------ */
  /*  Collection filter bar                                             */
  /* ------------------------------------------------------------------ */

  var filterBar = document.querySelector('.filter-bar');
  var filterForm = document.querySelector('.js-filter-form');
  var productGrid = document.querySelector('.js-product-grid');

  if (filterBar) {
    var filterCollapseThreshold = filterBar.offsetTop + filterBar.offsetHeight;
    window.addEventListener('scroll', function () {
      filterBar.classList.toggle('filter-bar--collapsed', window.scrollY > filterCollapseThreshold);
    }, { passive: true });
  }

  if (filterForm && productGrid) {
    filterForm.addEventListener('change', debounce(function () {
      var formData = new FormData(filterForm);
      var params = new URLSearchParams(formData).toString();
      var baseUrl = filterForm.getAttribute('action') || window.location.pathname;
      var url = baseUrl + '?' + params;
      var sectionId = productGrid.getAttribute('data-section-id');

      fetchSection(url, sectionId)
        .then(function (html) {
          /* Parse the returned HTML and extract the product grid */
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var newGrid = tmp.querySelector('.js-product-grid');
          if (newGrid) productGrid.innerHTML = newGrid.innerHTML;
          history.pushState(null, '', url);
        })
        .catch(function (err) { console.error('Filter fetch failed', err); });
    }, 400));
  }

  /* Sort dropdown (collection pages) */
  var sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var selectedValue = sortSelect.value;
      if (!selectedValue) return;
      var collectionUrl = (filterForm && filterForm.getAttribute('action'))
        || window.location.pathname;
      window.location.href = collectionUrl + '?sort_by=' + selectedValue;
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Mobile filter drawer (bottom-sheet)                               */
  /* ------------------------------------------------------------------ */

  var filterDrawer = document.querySelector('.collection-filters');
  var filterOverlay = document.getElementById('mobile-filter-overlay');
  var filterTrigger = document.getElementById('mobile-filter-trigger');
  var filterClose = document.querySelector('.collection-filters__drawer-close');

  if (filterDrawer && filterTrigger) {
    function openFilterDrawer() {
      filterDrawer.classList.add('is-open');
      if (filterOverlay) {
        filterOverlay.classList.add('is-visible');
      }
      filterTrigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('filter-drawer-open');

      /* Focus the close button */
      if (filterClose) filterClose.focus();

      /* Trap focus inside drawer */
      filterDrawer.addEventListener('keydown', trapFilterFocus);
    }

    function closeFilterDrawer() {
      filterDrawer.classList.remove('is-open');
      if (filterOverlay) {
        filterOverlay.classList.remove('is-visible');
      }
      filterTrigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('filter-drawer-open');

      filterDrawer.removeEventListener('keydown', trapFilterFocus);

      /* Return focus to trigger */
      filterTrigger.focus();
    }

    function trapFilterFocus(e) {
      if (e.key !== 'Tab') return;
      var focusable = filterDrawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    filterTrigger.addEventListener('click', function () {
      if (filterDrawer.classList.contains('is-open')) {
        closeFilterDrawer();
      } else {
        openFilterDrawer();
      }
    });

    if (filterClose) {
      filterClose.addEventListener('click', closeFilterDrawer);
    }

    if (filterOverlay) {
      filterOverlay.addEventListener('click', closeFilterDrawer);
    }

    /* Close on Escape */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && filterDrawer.classList.contains('is-open')) {
        closeFilterDrawer();
      }
    });

    /* Close drawer if viewport grows past mobile breakpoint */
    if (window.matchMedia) {
      var mql = window.matchMedia('(min-width: 768px)');
      function handleResize(e) {
        if (e.matches && filterDrawer.classList.contains('is-open')) {
          closeFilterDrawer();
        }
      }
      if (mql.addEventListener) {
        mql.addEventListener('change', handleResize);
      } else if (mql.addListener) {
        mql.addListener(handleResize);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Announcement bar dismiss                                          */
  /* ------------------------------------------------------------------ */

  var announcementBar = document.querySelector('.announcement-bar');
  var announceDismiss = document.querySelector('.js-announcement-dismiss');
  if (announcementBar) {
    if (sessionStorage.getItem('announcement_dismissed')) {
      announcementBar.style.display = 'none';
    } else if (announceDismiss) {
      announceDismiss.addEventListener('click', function () {
        announcementBar.style.display = 'none';
        sessionStorage.setItem('announcement_dismissed', '1');
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Email popup                                                       */
  /* ------------------------------------------------------------------ */

  var emailPopup = document.querySelector('.email-popup');
  var releaseFocusPopup;

  function openEmailPopup() {
    if (!emailPopup) return;
    /* Respect 7-day localStorage dismissal */
    var dismissed = localStorage.getItem('email_popup_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return;
    emailPopup.classList.add('email-popup--open');
    releaseFocusPopup = trapFocus(emailPopup);
  }

  function closeEmailPopup() {
    if (!emailPopup) return;
    emailPopup.classList.remove('email-popup--open');
    localStorage.setItem('email_popup_dismissed', String(Date.now()));
    if (typeof releaseFocusPopup === 'function') releaseFocusPopup();
  }

  if (emailPopup) {
    var popupDelay = parseInt(emailPopup.getAttribute('data-delay') || '5000', 10);
    setTimeout(openEmailPopup, popupDelay);

    /* Exit intent — mouse leaves top of viewport */
    document.addEventListener('mouseleave', function (e) {
      if (e.clientY <= 0) openEmailPopup();
    });

    /* Close buttons */
    emailPopup.addEventListener('click', function (e) {
      if (e.target.closest('.js-email-popup-close') || e.target.classList.contains('email-popup__overlay')) closeEmailPopup();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && emailPopup.classList.contains('email-popup--open')) closeEmailPopup();
    });

    /* Form submit */
    var popupForm = emailPopup.querySelector('form');
    if (popupForm) {
      popupForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var formData = new FormData(popupForm);
        fetch(popupForm.action || '/contact', { method: 'POST', body: formData })
          .then(function () {
            var successEl = emailPopup.querySelector('.js-email-popup-success');
            var formEl = emailPopup.querySelector('.js-email-popup-form');
            if (formEl) formEl.style.display = 'none';
            if (successEl) successEl.style.display = '';
          })
          .catch(function () {
            var errorEl = emailPopup.querySelector('.js-email-popup-error');
            if (errorEl) { errorEl.textContent = 'Something went wrong. Please try again.'; errorEl.style.display = ''; }
          });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Quick view modal                                                  */
  /* ------------------------------------------------------------------ */

  var quickViewModal = document.querySelector('.quick-view-modal');
  var quickViewContent = document.querySelector('.quick-view-modal__content');
  var releaseFocusQuickView;

  function openQuickView(productUrl) {
    if (!quickViewModal || !quickViewContent) return;
    quickViewContent.innerHTML = '<div class="quick-view-loading">Loading...</div>';
    quickViewModal.classList.add('quick-view-modal--open');
    releaseFocusQuickView = trapFocus(quickViewModal);

    fetchSection(productUrl, 'product-quick-view')
      .then(function (html) { quickViewContent.innerHTML = html; })
      .catch(function () { quickViewContent.innerHTML = '<p>Unable to load product. Please try again.</p>'; });
  }

  function closeQuickView() {
    if (!quickViewModal) return;
    quickViewModal.classList.remove('quick-view-modal--open');
    if (typeof releaseFocusQuickView === 'function') releaseFocusQuickView();
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-quick-view');
    if (btn) {
      e.preventDefault();
      var url = btn.getAttribute('data-product-url') || btn.getAttribute('href');
      if (url) openQuickView(url);
      return;
    }
    if (e.target.closest('.js-quick-view-close') || (quickViewModal && e.target === quickViewModal)) closeQuickView();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && quickViewModal && quickViewModal.classList.contains('quick-view-modal--open')) closeQuickView();
  });

  /* ------------------------------------------------------------------ */
  /*  FAQ accordion                                                     */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var question = e.target.closest('.js-faq-question');
    if (!question) return;
    var faqItem = question.closest('.faq-item');
    if (!faqItem) return;
    var answer = faqItem.querySelector('.faq-item__answer');
    var isOpen = faqItem.classList.contains('faq--open');

    if (isOpen) {
      /* Collapse */
      answer.style.height = answer.scrollHeight + 'px';
      /* Force reflow */
      answer.offsetHeight; // eslint-disable-line no-unused-expressions
      answer.style.height = '0';
      faqItem.classList.remove('faq--open');
    } else {
      /* Expand */
      faqItem.classList.add('faq--open');
      answer.style.height = answer.scrollHeight + 'px';
      answer.addEventListener('transitionend', function handler() {
        answer.style.height = 'auto';
        answer.removeEventListener('transitionend', handler);
      });
    }
  });

}); /* end DOMContentLoaded */
